import { getUserFriendlyErrorMessage } from '@/utils/errorHandler.js';

export default {
  // ── Get current user session (called on app mount) ────────────────────────
  async getMe({ commit }) {
    if (!commit) return;
    try {
      const response = await fetch(`/api/users/me`);

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("user");
        commit("setAuthStatus", false);
        return null;
      }

      let profile;
      try {
        profile = await response.json();
      } catch {
        commit("setAuthStatus", false);
        return null;
      }

      if (profile.statusCode === 401 || profile.statusCode === 403) {
        localStorage.removeItem("user");
        commit("setAuthStatus", false);
      } else {
        localStorage.setItem("user", JSON.stringify(profile));
        commit("setUserData", profile);
        commit("setAuthStatus", true);
      }
      return profile;
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      console.error('Authentication error:', friendlyMessage);
      commit("setAuthStatus", false);
      return null;
    }
  },

  // ── Step 1: Send OTP ──────────────────────────────────────────────────────
  // POST /auth/login-sms/send
  // Body (phone tab): { phone: "+12133734253", type: "login" }
  // Body (email tab):  { email: "user@example.com", type: "login" }
  // 200 OK: { phone, response: "OTP sent", status_id: 1 }
  async sendSmsCode({ commit }, { phone, email, captchaToken, type }) {
    try {
      const response = await fetch(`/api/auth/login-sms/send`, {
        method: "POST",
        body: JSON.stringify(email ? { email, type } : { phone, type }),
        referrer: window.location.origin,
        headers: {
          "Content-Type": "application/json",
          ...(captchaToken && { "X-Captcha-Token": captchaToken }),
        },
      });

      const data = await response.json();

      // status_id: 1 means OTP sent successfully
      if (response.ok && data.status_id === 1) {
        commit("setShowSmsCodeInput", true);
        return { status: "success", message: data.response || "OTP sent" };
      }

      return {
        status: "error",
        message: data.reason || data.message || "Failed to send OTP",
      };
    } catch (error) {
      return { status: "error", message: "Server error. Please try again." };
    }
  },

  // ── Step 2: Verify OTP ────────────────────────────────────────────────────
  // POST /auth/login-sms/check
  // Body (phone tab): { code, phone, email: <stored email>, ... }
  // Body (email tab):  { code, email: <typed email>, ... } (no phone key)
  // 200 OK: { response: { id, firstName, lastName, email, phone, username, rating, celebrity, verified, created }, status_id: 1 }
  async verifySmsCode({ commit }, { phone, email, code }) {
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      const response = await fetch(`/api/auth/login-sms/check`, {
        method: "POST",
        body: JSON.stringify({
          code,
          phone: email ? undefined : phone,
          email: email || stored.email || "",
          firstName: stored.firstName || "",
          lastName: stored.lastName || "",
          username: stored.username || "",
          invitedByUserId: stored.invitedByUserId || "",
          frequency: stored.frequency || {},
          phone_verification_required: true,
        }),
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();

      // API error (non-2xx or explicit error field)
      if (!response.ok || data.status_id === 0) {
        commit("setUserDataError", data.reason || data.message || "Invalid OTP");
        return {
          status: "error",
          message: data.reason || data.message || "Invalid OTP. Please try again.",
        };
      }

      // Success: user object is nested under data.response
      const user = data.response;
      if (data.status_id === 1 && user?.id) {
        // OLD: stored raw response directly (nulls not handled)
        // localStorage.setItem("user", JSON.stringify(user));
        // commit("setUserData", user);
        // commit("setAuthStatus", true);
        // return { status: "success", message: "" };

        // NEW: normalize response fields — use empty strings for missing/null values
        const normalizedUser = {
          id: user.id || "",
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: user.phone || "",
          username: user.username || "",
          rating: user.rating ?? 0,
          celebrity: user.celebrity ?? false,
          verified: user.verified ?? false,
          created: user.created ?? false,
          invitedByUserId: user.invitedByUserId || "none",
          frequency: user.frequency || {},
          phone_verification_required: user.phone_verification_required ?? true,
        };
        localStorage.setItem("user", JSON.stringify(normalizedUser));
        commit("setUserData", normalizedUser);
        commit("setAuthStatus", true);
        return { status: "success", message: "" };
      }

      // Unexpected response shape
      commit("setUserDataError", data.reason || data.message || "Unexpected response");
      return {
        status: "error",
        message: data.reason || data.message || "Sign-in failed. Please try again.",
      };
    } catch (error) {
      commit("setUserDataError", error.message);
      return { status: "error", message: "Sign-in request failed. Please retry." };
    }
  },

  // ── Logout ────────────────────────────────────────────────────────────────
  async logOut({ commit }) {
    try {
      await fetch(`/api/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch {}
    commit("setAuthStatus", false);
    commit("setUserData", null);
    commit("setProfile", null);
    localStorage.removeItem("user");
    localStorage.removeItem("activeTab");
    localStorage.removeItem("isFirstLogin");
    window.location.replace("/sign-in");
  },

  // ── Helpers ───────────────────────────────────────────────────────────────
  async addAuthStatus({ commit }, status) {
    commit("setAuthStatus", status);
  },

  resetShowSmsCodeInput({ commit }) {
    commit("setShowSmsCodeInput", false);
  },

  // ── COMMENTED: unused in admin panel ─────────────────────────────────────
  // signUp, logIn (password), verifyTwoFaLogin, logInWithX, fetchDisconnectX,
  // authWithX, deleteCurrentUser, siwfCallback — not needed for moderation panel
};
